import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { TASKBAR_HEIGHT } from "@/shared/ui";
import { IconPositionService } from "@/services/icon-position-service";
import { logInfo } from "@/lib/logger";

import {
  ICON_SIZE,
  GRID_CELL_WIDTH,
  GRID_CELL_HEIGHT,
  makeCellKey,
  clampCellToBounds,
  scanFirstFreeCell,
  resolveDisplayPositions,
  findNearestFreePosition as unifiedFindNearestFreePosition,
  DESKTOP_AREA_PADDING,
} from "@/utils/desktop-layout-utils";

const getInitialViewport = () => ({
  width: typeof window === "undefined" ? 0 : window.innerWidth,
  height: typeof window === "undefined" ? 0 : window.innerHeight,
});

type Size = {
  width: number;
  height: number;
};

export type GridContainerSize = Size;

const CELL_X_OFFSET = Math.max((GRID_CELL_WIDTH - ICON_SIZE.width) / 2, 0);
const CELL_Y_OFFSET = Math.max((GRID_CELL_HEIGHT - ICON_SIZE.height) / 2, 0);

export type GridPosition = {
  row: number;
  col: number;
};

export type IconPositions = Record<string, GridPosition>;

const normalizeDimension = (value: number): number => {
  if (!Number.isFinite(value) || value < 1) {
    return 1;
  }

  return Math.floor(value);
};

const DESKTOP_CONTAINER_SELECTOR = '[data-driver-id="desktop-icons-area"]';

/**
 * Stabile Serialisierung der Positions-Map für Änderungsvergleiche,
 * unabhängig von der Key-Reihenfolge.
 */
const serializePositions = (positions: IconPositions): string =>
  JSON.stringify(
    Object.keys(positions)
      .sort()
      .map((appId) => [appId, positions[appId].row, positions[appId].col])
  );

/**
 * Lädt Icon-Positionen aus dem Backend.
 * Wirft bei Backend-Fehlern, damit der Aufrufer das Speichern sperren kann —
 * sonst würde ein leerer State das gespeicherte Layout überschreiben (#376).
 */
const loadStoredPositionsAsync = async (): Promise<IconPositions> => {
  const positions = await IconPositionService.loadIconPositions();

  const validated: IconPositions = {};
  Object.entries(positions).forEach(([appId, value]) => {
    if (!value || typeof value !== "object") {
      return;
    }

    if (typeof value.row === "number" && typeof value.col === "number") {
      validated[appId] = {
        row: Math.max(0, Math.floor(value.row)),
        col: Math.max(0, Math.floor(value.col)),
      };
    }
  });

  logInfo(
    `Icon-Positionen aus Backend geladen: ${
      Object.keys(validated).length
    } Icons`
  );
  return validated;
};

/**
 * Steuert die Positionierung der Desktop-Icons auf einem Raster.
 *
 * Persistenz-Modell:
 * - `storedPositions` ist das vom User beabsichtigte Layout (Backend,
 *   user_settings.settings_data.desktopIconPositions). Es enthält auch
 *   Positionen von Apps, die gerade nicht sichtbar sind, und wird nur durch
 *   explizite Aktionen verändert (Drag & Drop, Platzierung neuer Icons,
 *   Reset) — nie durch Login-Races, Resizes oder transient leere App-Listen
 *   (#376, #382).
 * - Gerendert werden abgeleitete Display-Positionen: pro sichtbarem Icon in
 *   die aktuellen Grid-Grenzen geclampt und kollisionsfrei aufgelöst (#377).
 *   Ein Wechsel auf ein kleineres Display verändert das gespeicherte Layout
 *   nicht — zurück auf dem größeren Display liegt alles wieder am alten Platz.
 */
export function useIconGridPosition(
  appIds: string[],
  externalContainerSize?: GridContainerSize,
  blockedCells: Set<string> = new Set()
) {
  // Convert Set to stable string for dependency tracking
  const blockedCellsKey = useMemo(
    () => Array.from(blockedCells).sort().join(","),
    [blockedCells]
  );

  const isBlockedCell = useCallback(
    (row: number, col: number) => blockedCells.has(makeCellKey(row, col)),
    [blockedCellsKey] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const [viewportSize, setViewportSize] = useState(getInitialViewport);
  const [containerSize, setContainerSize] = useState<Size>({
    width: 0,
    height: 0,
  });
  const [storedPositions, setStoredPositions] = useState<IconPositions>({});
  const [isLoadingPositions, setIsLoadingPositions] = useState(true);
  // Speichern ist erst nach einem erfolgreichen Load erlaubt
  const canPersistRef = useRef(false);
  const lastSavedRef = useRef<string>(serializePositions({}));
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load positions from backend on mount
  useEffect(() => {
    let cancelled = false;

    const loadPositions = async () => {
      try {
        const loaded = await loadStoredPositionsAsync();

        if (!cancelled) {
          setStoredPositions(loaded);
          lastSavedRef.current = serializePositions(loaded);
          canPersistRef.current = true;
        }
      } catch (error) {
        console.error("[useIconGridPosition] Failed to load positions:", error);
      } finally {
        if (!cancelled) {
          setIsLoadingPositions(false);
        }
      }
    };

    loadPositions();

    return () => {
      cancelled = true;
    };
  }, []);

  // Save stored positions to backend (debounced, nur bei echten Änderungen).
  // Die Map wird komplett gespeichert — auch Positionen gerade nicht
  // sichtbarer Apps bleiben erhalten (#376). Entfernt werden Einträge nur
  // durch explizite Aktionen (App-Deinstallation, Reset).
  useEffect(() => {
    if (isLoadingPositions || !canPersistRef.current) {
      return;
    }

    const serialized = serializePositions(storedPositions);
    if (serialized === lastSavedRef.current) {
      return;
    }

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(async () => {
      try {
        const success = await IconPositionService.saveIconPositions(
          storedPositions
        );
        if (success) {
          lastSavedRef.current = serialized;
          logInfo(
            `Icon-Positionen im Backend gespeichert: ${
              Object.keys(storedPositions).length
            } Icons`
          );
        }
      } catch (error) {
        console.error(
          "[useIconGridPosition] Failed to save icon positions to backend:",
          error
        );
      }
    }, 500);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [storedPositions, isLoadingPositions]);

  const updateContainerSize = useCallback((width: number, height: number) => {
    const normalizedWidth = Math.max(width, 0);
    const normalizedHeight = Math.max(height, 0);

    setContainerSize((prev) => {
      if (
        Math.abs(prev.width - normalizedWidth) < 0.5 &&
        Math.abs(prev.height - normalizedHeight) < 0.5
      ) {
        return prev;
      }
      return { width: normalizedWidth, height: normalizedHeight };
    });
  }, []);

  const measureContainer = useCallback(() => {
    if (externalContainerSize) {
      return null;
    }
    if (typeof document === "undefined") {
      return null;
    }
    const element = document.querySelector(
      DESKTOP_CONTAINER_SELECTOR
    ) as HTMLElement | null;
    if (element) {
      updateContainerSize(element.clientWidth, element.clientHeight);
    }
    return element;
  }, [externalContainerSize, updateContainerSize]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    let frame: number | null = null;

    const handleResize = () => {
      if (frame) {
        cancelAnimationFrame(frame);
      }
      frame = window.requestAnimationFrame(() => {
        const width = window.innerWidth;
        const height = window.innerHeight;
        setViewportSize({ width, height });
        measureContainer();
      });
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      if (frame) {
        cancelAnimationFrame(frame);
      }
      window.removeEventListener("resize", handleResize);
    };
  }, [measureContainer]);

  useEffect(() => {
    if (!externalContainerSize) {
      return;
    }
    updateContainerSize(
      externalContainerSize.width,
      externalContainerSize.height
    );
  }, [
    externalContainerSize?.height,
    externalContainerSize?.width,
    updateContainerSize,
  ]);

  useEffect(() => {
    if (externalContainerSize) {
      return;
    }
    if (typeof window === "undefined" || typeof document === "undefined") {
      return;
    }

    let animationFrame: number | null = null;
    let resizeObserver: ResizeObserver | null = null;
    let resizeListenerCleanup: (() => void) | null = null;
    let cancelled = false;

    const updateSize = (width: number, height: number) => {
      if (cancelled) {
        return;
      }
      updateContainerSize(width, height);
    };

    const attachObserver = () => {
      if (cancelled) {
        return;
      }

      const element = measureContainer();
      if (!element) {
        animationFrame = window.requestAnimationFrame(attachObserver);
        return;
      }

      if (typeof ResizeObserver !== "undefined") {
        resizeObserver = new ResizeObserver((entries) => {
          const entry = entries[0];
          if (!entry) {
            return;
          }
          updateSize(entry.contentRect.width, entry.contentRect.height);
        });
        resizeObserver.observe(element);
      } else {
        const handleResize = () =>
          updateSize(element.clientWidth, element.clientHeight);
        window.addEventListener("resize", handleResize);
        resizeListenerCleanup = () =>
          window.removeEventListener("resize", handleResize);
      }
    };

    attachObserver();

    return () => {
      cancelled = true;
      if (animationFrame) {
        window.cancelAnimationFrame(animationFrame);
      }
      resizeObserver?.disconnect();
      resizeListenerCleanup?.();
    };
  }, [externalContainerSize, measureContainer, updateContainerSize]);

  const containerWidth = externalContainerSize?.width ?? containerSize.width;
  const containerHeight = externalContainerSize?.height ?? containerSize.height;

  const getGridDimensions = useCallback(() => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return {
        rows: 1,
        cols: 1,
        gridCellWidth: GRID_CELL_WIDTH,
        gridCellHeight: GRID_CELL_HEIGHT,
        cellXOffset: CELL_X_OFFSET,
        cellYOffset: CELL_Y_OFFSET,
      };
    }

    const fallbackWidth = Math.max(
      viewportSize.width - DESKTOP_AREA_PADDING,
      GRID_CELL_WIDTH
    );
    const fallbackHeight = Math.max(
      viewportSize.height - TASKBAR_HEIGHT - DESKTOP_AREA_PADDING,
      GRID_CELL_HEIGHT
    );

    let availableWidth = containerWidth;
    let availableHeight = containerHeight;

    if (availableWidth <= 0 || availableHeight <= 0) {
      const element = document.querySelector(
        DESKTOP_CONTAINER_SELECTOR
      ) as HTMLElement | null;
      if (element) {
        if (availableWidth <= 0) {
          availableWidth = element.clientWidth;
        }
        if (availableHeight <= 0) {
          availableHeight = element.clientHeight;
        }
      }
    }

    if (availableWidth <= 0) {
      availableWidth = fallbackWidth;
    }

    if (availableHeight <= 0) {
      availableHeight = fallbackHeight;
    }

    const effectiveWidth = Math.max(
      GRID_CELL_WIDTH,
      Math.min(availableWidth, fallbackWidth)
    );
    const effectiveHeight = Math.max(
      GRID_CELL_HEIGHT,
      Math.min(availableHeight, fallbackHeight)
    );

    // Calculate maximum possible columns and rows based on minimum cell size
    const maxCols = Math.max(
      1,
      normalizeDimension(effectiveWidth / GRID_CELL_WIDTH)
    );
    const maxRows = Math.max(
      1,
      normalizeDimension(effectiveHeight / GRID_CELL_HEIGHT)
    );

    // Check if we can fit all icons in the available space
    const totalIcons = Math.max(appIds.length, 1);
    const canFitInMaxSpace = totalIcons <= maxCols * maxRows;

    let cols: number;
    let rows: number;

    if (canFitInMaxSpace) {
      // Solange alle Icons passen, hängen Raster und Zellgröße nur von der
      // Containergröße ab — das Hinzufügen eines Icons verschiebt sonst das
      // gesamte Layout, weil sich die Zellhöhe ändert (#382)
      cols = maxCols;
      rows = maxRows;
    } else {
      // Not enough space with current layout
      // Prioritize fitting all icons by increasing columns if height is limited
      const minRowsNeeded = maxRows;
      const minColsNeeded = Math.ceil(totalIcons / minRowsNeeded);

      cols = Math.max(maxCols, minColsNeeded);
      rows = maxRows;
    }

    // Calculate dynamic cell sizes to fill the entire desktop area
    // This distributes any remaining space evenly across all cells
    const dynamicCellWidth = effectiveWidth / cols;
    const dynamicCellHeight = effectiveHeight / rows;

    // Calculate new offsets for centering icons within the dynamic cells
    const dynamicCellXOffset = Math.max((dynamicCellWidth - ICON_SIZE.width) / 2, 0);
    const dynamicCellYOffset = Math.max((dynamicCellHeight - ICON_SIZE.height) / 2, 0);

    return {
      rows,
      cols,
      gridCellWidth: dynamicCellWidth,
      gridCellHeight: dynamicCellHeight,
      cellXOffset: dynamicCellXOffset,
      cellYOffset: dynamicCellYOffset,
    };
  }, [
    appIds.length,
    containerHeight,
    containerWidth,
    viewportSize.height,
    viewportSize.width,
  ]);

  const gridDimensions = useMemo(
    () => getGridDimensions(),
    [getGridDimensions]
  );

  // Abgeleitete, kollisionsfreie Render-Positionen der sichtbaren Icons.
  // Das gespeicherte Layout bleibt unangetastet, damit ein Wechsel auf ein
  // kleineres Display es nicht dauerhaft zerstört (#377).
  const displayPositions = useMemo(
    () =>
      resolveDisplayPositions(
        storedPositions,
        appIds,
        gridDimensions,
        blockedCells
      ),
    [storedPositions, appIds, gridDimensions, blockedCellsKey] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const displayPositionsRef = useRef<IconPositions>(displayPositions);
  useEffect(() => {
    displayPositionsRef.current = displayPositions;
  }, [displayPositions]);

  // Neuen sichtbaren Apps ohne gespeicherte Position eine freie Zelle
  // zuweisen. Bestehende Einträge werden dabei nie verändert oder entfernt —
  // auch nicht bei transient leerer oder unvollständiger App-Liste während
  // Login/Settings-Reload (#376, #382).
  useEffect(() => {
    if (isLoadingPositions || !appIds.length) {
      return;
    }

    setStoredPositions((prev) => {
      const missing = appIds.filter((appId) => !prev[appId]);
      if (!missing.length) {
        return prev;
      }

      const next: IconPositions = { ...prev };
      const occupied = new Set<string>(blockedCells);
      appIds.forEach((appId) => {
        const position = next[appId];
        if (position) {
          const clamped = clampCellToBounds(position, gridDimensions);
          occupied.add(makeCellKey(clamped.row, clamped.col));
        }
      });

      missing.forEach((appId) => {
        const free = scanFirstFreeCell(occupied, gridDimensions);
        next[appId] = free;
        occupied.add(makeCellKey(free.row, free.col));
      });

      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appIds, isLoadingPositions, gridDimensions, blockedCellsKey]);

  const findFirstFreePosition = useCallback(
    (existingPositions?: IconPositions): GridPosition => {
      const current = existingPositions ?? displayPositionsRef.current;
      const occupied = new Set<string>(blockedCells);
      Object.values(current).forEach((pos) =>
        occupied.add(makeCellKey(pos.row, pos.col))
      );
      return scanFirstFreeCell(occupied, gridDimensions);
    },
    [gridDimensions, blockedCellsKey] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const isCellOccupied = useCallback(
    (row: number, col: number): boolean => {
      if (isBlockedCell(row, col)) {
        return true;
      }
      return Object.values(displayPositionsRef.current).some(
        (pos) => pos.row === row && pos.col === col
      );
    },
    [isBlockedCell]
  );

  const setIconPosition = useCallback(
    (appId: string, position: GridPosition): boolean => {
      const target = clampCellToBounds(position, gridDimensions);

      // Kollisionsprüfung gegen die sichtbaren (Display-)Positionen der
      // anderen Icons, damit nie zwei Icons auf derselben Zelle landen
      const occupiedKeys = new Set<string>(blockedCells);
      Object.entries(displayPositionsRef.current).forEach(([id, pos]) => {
        if (id !== appId) {
          occupiedKeys.add(makeCellKey(pos.row, pos.col));
        }
      });

      let finalTarget = target;
      if (occupiedKeys.has(makeCellKey(target.row, target.col))) {
        finalTarget =
          unifiedFindNearestFreePosition(
            target,
            { rows: 1, cols: 1 },
            gridDimensions,
            occupiedKeys
          ) || target;
      }

      // Immer ein neues Objekt, damit React die Änderung erkennt und
      // framer-motion den Drag-Offset zurücksetzt
      setStoredPositions((prev) => ({
        ...prev,
        [appId]: { ...finalTarget },
      }));

      return true;
    },
    [gridDimensions, blockedCellsKey] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const getIconPosition = useCallback(
    (appId: string): GridPosition | undefined => displayPositions[appId],
    [displayPositions]
  );

  const gridToPixels = useCallback(
    (position: GridPosition) => {
      // Use dynamic cell sizes from gridDimensions
      const { gridCellWidth, gridCellHeight, cellXOffset, cellYOffset } =
        gridDimensions;
      return {
        x: position.col * gridCellWidth + cellXOffset,
        y: position.row * gridCellHeight + cellYOffset,
      };
    },
    [gridDimensions]
  );

  const pixelsToGrid = useCallback(
    (x: number, y: number): GridPosition => {
      // Use dynamic cell sizes from gridDimensions
      const { gridCellWidth, gridCellHeight, rows, cols } = gridDimensions;

      // Use center of the icon for detection
      const centerX = x + ICON_SIZE.width / 2;
      const centerY = y + ICON_SIZE.height / 2;

      const col = Math.floor(centerX / gridCellWidth);
      const row = Math.floor(centerY / gridCellHeight);

      // Clamp to grid bounds
      return {
        row: Math.max(0, Math.min(row, Math.max(rows - 1, 0))),
        col: Math.max(0, Math.min(col, Math.max(cols - 1, 0))),
      };
    },
    [gridDimensions]
  );

  // Die Persistenz läuft über den debounced Save-Effekt; bei fehlgeschlagenem
  // Load bleibt das Backend-Layout unangetastet
  const resetPositions = useCallback(async () => {
    setStoredPositions({});
    logInfo("Icon-Positionen zurückgesetzt");
  }, []);

  return {
    positions: displayPositions,
    getIconPosition,
    setIconPosition,
    isCellOccupied,
    findFirstFreePosition,
    gridToPixels,
    pixelsToGrid,
    resetPositions,
    gridDimensions,
  };
}
